export type FeatureArea = "signatures" | "listings" | "customers" | "ai" | "messaging" | "minisites" | "meetings" | "account";

export interface FeatureUpdate {
  id: number;
  title: string;
  content: string;
  date: string;
  areas?: FeatureArea[];
  showOnlyForRealEstate?: boolean; // Whether this update should only be shown to real estate users
  /**
   * A big release, opened by itself once for every customer who has not seen it
   * (featureUpdatePopups.ts). Set ONLY on Haim's explicit go in the session: it reaches every
   * customer the moment it deploys.
   */
  popup?: boolean;
}

export const STATIC_FEATURE_UPDATES: FeatureUpdate[] = [
  {
    id: 68,
    areas: ["listings", "meetings"],
    date: '04-11-2026',
    showOnlyForRealEstate: true,
    title: 'פגישות ומשימות על כל נכס במבט אחד',
    content: `ליד כל נכס בלוח הנכסים מופיע עכשיו **סימן יומן**: כחול כשיש פגישה או משימה בהמשך, אדום כשמשימה באיחור, ואפור כשכל הפעילות כבר עברה. מעבר עם העכבר מראה מה הבא בתור, ולחיצה פותחת את כל הפגישות והמשימות על הנכס, שלכם ושל המשרד.

בחלון הנכס נוסף החלק **פעילות על הנכס**: הפגישות, המשימות וההערות ברשימה אחת.`
  },
  {
    id: 67,
    areas: ["listings", "customers"],
    date: '04-11-2026',
    showOnlyForRealEstate: true,
    title: 'בעל הנכס והנכס שלו במקום אחד',
    content: `אחרי ששומרים בעל נכס מ**לוח הנכסים**, השורה מציגה את **השם והמספר ששמרתם** במקום המספר הווירטואלי של יד2, וכך גם כרטיס הנכס. משימה או פגישה שיוצרים מהשורה נקבעת מול בעל הנכס, במספר האמיתי שלו.

ההערות שכותבים על הנכס מופיעות גם ב**כרטיס הלקוח** של בעל הנכס, עם כתובת הנכס. לחיצה על הסמל הירוק ליד המספר פותחת את כרטיס הלקוח בחלון צד, בלי לעזוב את הלוח.`
  },
  {
    id: 66,
    areas: ["listings", "meetings"],
    date: '02-11-2026',
    showOnlyForRealEstate: true,
    title: 'תזכורת לברך על הכניסה לנכס',
    content: `כשמסמנים נכס כ**נמכר**, אפשר להוסיף באותו חלון **תזכורת לברך על הכניסה לנכס**. בוחרים מי מהמתווכים בצד המוכר יקבל אותה, ומתי: **ביום הכניסה** (כשתאריך הכניסה רשום בנכס), **בעוד שבוע**, **בעוד חודש** או בתאריך אחר.

התזכורת נכנסת ליומן ולרשימת המשימות של המתווך שנבחר, מקושרת לנכס ולמוכר. ללקוח עצמו לא נשלח דבר.`
  },
  {
    id: 65,
    areas: ["listings", "messaging"],
    date: '30-10-2026',
    showOnlyForRealEstate: true,
    title: 'ירידות מחיר וסיכום יומי בתמונה',
    content: `ירידות המחיר של היום מגיעות ב-**18:00** בתמונה אחת, במקום הודעה על כל ירידה. מי שבחר **הודעה מרכזת אחת ביום** מקבל ב-**19:00** סיכום של היום: נכסים חדשים, נכסים מבעלים פרטיים וירידות מחיר, עם שלושת הבולטים.

<img src="/static/_haim/price-drops-card-update.jpg" alt="ירידות המחיר של היום בתמונה אחת: המחיר הקודם, המחיר החדש ושיעור הירידה" style="max-width: 100%; border-radius: 8px; margin: 12px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08);" />

אחרי **21:00** לא נשלחות התראות על נכסים חדשים, והן מחכות להודעה של הבוקר.`
  },
  {
    id: 64,
    areas: ["meetings", "messaging"],
    date: '30-10-2026',
    showOnlyForRealEstate: true,
    title: 'סדר היום בתמונה אחת',
    content: `התזכורות של **08:00** ו-**20:00** מגיעות עכשיו כ**תמונה אחת** עם כל הפגישות והמשימות של היום (ובערב, של מחר), במקום הודעה על כל פגישה. גם התזכורת **שעה לפני** פגישה מגיעה כתמונה, עם תמונת הנכס כשהפגישה בנכס.

<img src="/static/_haim/agenda-card-update.jpg" alt="סדר היום בוואטסאפ: כל הפגישות והמשימות של היום בתמונה אחת" style="max-width: 100%; border-radius: 8px; margin: 12px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08);" />

הלקוחות שלכם מקבלים את הזימון, העדכון והביטול ככרטיס עם **התמונה, השם והלוגו שלכם**, ו**תזכורת אחת** לכל פגישה. את התמונה והלוגו מעדכנים ב**הגדרות ← תמונת משתמש ולוגו המשרד**.`
  },
  {
    id: 63,
    areas: ["ai", "minisites"],
    date: '30-10-2026',
    showOnlyForRealEstate: true,
    title: 'דניאלה מנהלת גם את המיניסייטים',
    content: `דניאלה יכולה עכשיו לעשות במיניסייט של כל לקוח את מה שעושים בדשבורד, ישירות מהוואטסאפ. כותבים לה מה צריך:

- **להוסיף או להוריד נכסים** מהמיניסייט של לקוח
- **לראות מה הלקוח עשה**: אילו נכסים פתח, אילו אהב ובאילו ביקר
- **לערוך איך נכס נראה לקונים**: התיאור, אילו תמונות מוסתרות, והאם להציג את השיפוצים הווירטואליים שלו. השינוי חל על כל המיניסייטים שבהם הנכס מופיע, גם בקישורים שכבר נשלחו
- **הערה פנימית** על נכס אצל לקוח מסוים, וסימון **ביקר בנכס**
- **הסרת סימני מים** מתמונות של נכס. בפעם הראשונה דניאלה תבקש שתאשרו שיש לכם את הזכויות לכך

לדוגמה: „תורידי מהמיניסייט של דנה את הדירה בהרצל", או „תסתירי את תמונה 3 בנכס בבן יהודה".`
  },
  {
    id: 62,
    areas: ["listings", "messaging"],
    date: '30-10-2026',
    showOnlyForRealEstate: true,
    title: 'התראות נכסים בהודעה אחת',
    content: `התראות הוואטסאפ על נכסים חדשים מגיעות עכשיו ב**הודעה אחת**: במקום הודעה על כל נכס, מקבלים תמונה אחת עם הנכסים החדשים של אותו עדכון. נכסים מבעלים פרטיים מופיעים ראשונים, וכשנכס מתאים לאחד הקונים שלכם מופיעים שמו ואחוז ההתאמה.

<img src="/static/_haim/listing-alert-card-update.jpg" alt="התראת נכסים בוואטסאפ: תמונה אחת עם הנכסים החדשים, נכסים מבעלים פרטיים ראשונים ושם הקונה המתאים" style="max-width: 100%; border-radius: 8px; margin: 12px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08);" />

בתמונה עד ארבעה נכסים, והשאר בכפתור **לכל הנכסים**. מעדיפים הודעה אחת ביום? בוחרים **הודעה מרכזת אחת ביום** ב**הגדרות ← התראות**.`
  },
  {
    id: 61,
    areas: ["customers", "messaging"],
    date: '29-10-2026',
    showOnlyForRealEstate: true,
    title: 'סינון ומיון לפי ההודעה האחרונה',
    content: `ב**לקוחות** אפשר עכשיו לסנן לפי הודעת הדיוור האחרונה שכל לקוח קיבל, מהוואטסאפ הרשמי או מהתוסף: ב**סינון מתקדם ← הודעה אחרונה** בוחרים **קיבל הודעה** או **לא קיבל הודעה**, ואת התקופה. **לא קיבל הודעה** כולל גם את מי שלא קיבל הודעה אף פעם.

כדי למיין, מוסיפים את העמודה **הודעה אחרונה** בכפתור **שדות בשורה** (סמל הכוונון שבפינת הטבלה) ולוחצים על הכותרת שלה: לחיצה אחת מציגה קודם את מי שקיבלו הודעה לאחרונה, ולחיצה נוספת את מי שהכי הרבה זמן לא קיבלו.`
  },
  {
    id: 60,
    areas: ["listings"],
    date: '28-10-2026',
    showOnlyForRealEstate: true,
    popup: true,
    title: 'חדש: פרסום אוטומטי בקבוצות פייסבוק',
    content: `<fbpostinghero></fbpostinghero>

בוחרים נכס וקבוצות פייסבוק, ו-Webot עושה את השאר: כותב את הפוסט בכמה נוסחים בעזרת AI, מתזמן אותו ומפרסם בשמכם מהדפדפן שלכם, דרך תוסף Webot לכרום. בלי להעתיק ולהדביק את אותו פוסט בכל קבוצה מחדש.

- **פוסטים שנכתבים לבד**, מפרטי הנכס ולפי ההנחיות שלכם, עם התמונות שלו
- **תזמון קבוע**: הנכס עולה שוב כל כמה ימים, בימים ובשעות שבחרתם
- **מעקב**: רואים מה פורסם, באיזו קבוצה ומתי, ומה מחכה לפרסום
- **אתם מאשרים הכל**: שום פוסט לא יוצא בלי שאישרתם אותו

<div style="background:rgba(24,119,242,0.12);border-right:4px solid #1877F2;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🚀 הבטא נפתחה:</strong> אנחנו מצרפים עכשיו את משתמשי הבטא הראשונים, ומספר המקומות מוגבל. הירשמו ונחזור אליכם כשיגיע תורכם.</div>

<fbpostingbeta></fbpostingbeta>

<p style="font-size:12px;opacity:0.7;text-align:center;margin-top:0;">* הפיצ'ר יינתן בתוספת תשלום</p>`
  },
  {
    id: 59,
    areas: ["messaging", "customers"],
    date: '21-10-2026',
    showOnlyForRealEstate: true,
    title: 'קל יותר לשלוח הודעות ללקוחות בוואטסאפ',
    content: `ב**לקוחות** מסמנים את מי שרוצים, לוחצים **שליחת הודעה** ובוחרים איך לשלוח: בוואטסאפ הרשמי או מהתוסף. בוואטסאפ הרשמי בוחרים תבנית, רואים בדיוק איך ההודעה תיראה ושולחים, בלי ליצור קהל.

<img src="/static/_haim/crm-whatsapp-send-update.jpg" alt="שליחת הודעה מעמוד הלקוחות: בוחרים וואטסאפ רשמי או את התוסף, והנכס והתמונות שלו נכנסים להודעה לבד" style="max-width: 100%; border-radius: 8px; margin: 12px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08);" />

- **תבנית על נכס?** בוחרים את הנכס, והפרטים והתמונות שלו נכנסים לבד. הקישור פותח את הנכס במיני-סייט של כל לקוח. בתבניות שלכם קובעים את זה ב**מקורות הערכים**, בתפריט של התבנית.
- **תבנית חדשה מחכה לכם: נכס חדש למתעניינים**, עם תמונת הנכס, הפרטים שלו וכפתור „לצפייה בנכס". ובתבנית שאתם בונים, **כפתור לנכס** מוסיף כפתור שפותח את הנכס שבחרתם בשליחה.`
  },
  {
    id: 58,
    areas: ["meetings"],
    date: '19-10-2026',
    showOnlyForRealEstate: true,
    title: 'משימות לחברי המשרד',
    content: `במשרד אפשר עכשיו להקצות משימות זה לזה. בטופס המשימה בוחרים בשדה **באחריות** את מי שיטפל במשימה, והיא עוברת ליומן ולרשימת המשימות של מי שבחרתם.
משימות שהקציתם מרוכזות ב**סדר יום**, בלשונית **הקצתי לאחרים**: רואים שם אם בוצעו, ואפשר לערוך אותן או לבטל.`
  },
  {
    id: 57,
    areas: ["signatures"],
    date: '19-10-2026',
    showOnlyForRealEstate: true,
    title: 'שליחת חוזה מהוואטסאפ שלכם',
    content: `אחרי הכנת חוזה אפשר ללחוץ על **שיתוף בוואטסאפ**: נפתח הצ'אט שלכם עם הלקוח, עם הודעה קצרה וקישור לחתימה, ואתם שולחים מהוואטסאפ שלכם. כך ההודעה מופיעה אצלכם בצ'אט.

**שליחה אוטומטית ב-WhatsApp וב-SMS** נשארת: היא יוצאת מהמספר של Webot ולכן לא מופיעה בוואטסאפ שלכם, ואחרי השליחה כתוב לאיזה מספר נשלחה. שתי האפשרויות זמינות גם ב**חתימות שנשלחו**, בכל מסמך שממתין לחתימה.`
  },
  {
    id: 56,
    areas: ["customers", "minisites"],
    date: '19-10-2026',
    showOnlyForRealEstate: true,
    popup: true,
    title: 'חדש: אתר אישי חדש וטופס לידים ✨',
    content: `### האתר האישי שלכם, בגרסה חדשה ✨

לכל מתווך ב-Webot יש **אתר אישי חדש**: הנכסים שלכם, הפרטים שלכם וכפתורי וואטסאפ ושיחה, בעיצוב שלכם. הוא מהיר, נראה מעולה בטלפון, ולכל נכס יש בו עמוד משלו עם גלריה ופרטים מלאים.

<img src="/static/_haim/realtor-site-update.jpg?v=2" alt="האתר האישי החדש, עמוד נכס ותצוגה מקדימה בוואטסאפ" style="max-width: 100%; border-radius: 8px; margin: 12px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08);" />

**🔗 תצוגה מקדימה בוואטסאפ**
כששולחים בוואטסאפ קישור לאתר או לנכס, מופיעה עכשיו תצוגה מקדימה: התמונה, המחיר, המיקום והשם שלכם. הלקוח רואה את הנכס עוד לפני שלחץ.

הקישורים ששלחתם בעבר ממשיכים לעבוד, ומובילים לאתר החדש.

### טופס לידים 📝

קישור אחד ששולחים ללקוחות, מוסיפים לפרסומים ולסטטוס: הלקוח עונה על כמה שאלות קצרות (קנייה או שכירות, אזורים, תקציב, חדרים ומה חייב להיות בנכס) ומקבל **מיד** נכסים שלכם שמתאימים לו.

<img src="/static/_haim/lead-form-update.jpg?v=2" alt="טופס הלידים: הלקוח פותח את הקישור, עונה על כמה שאלות ומקבל נכסים שמתאימים לו" style="max-width: 100%; border-radius: 8px; margin: 12px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08);" />

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> במקום לשאול כל לקוח את אותן שאלות בטלפון, הוא מספר הכל בעצמו בשתי דקות, ואתם מקבלים כרטיס לקוח מלא עם כל מה שחשוב לו.</div>

**ומה קורה אצלכם?**
- מי שממלא **נכנס ישר ללקוחות שלכם**, בסטטוס **חדש** ועם המקור **טופס לידים**
- על כל ליד מגיעה אליכם **הודעה בוואטסאפ**
- לקוח שכבר במערכת? **הכרטיס שלו מתעדכן** במה שמילא, וכל שינוי נרשם בהערות

<img src="/static/_haim/lead-form-crm-update.jpg" alt="הודעה בוואטסאפ על כל ליד, וכל מה שהלקוח סיפר נרשם בכרטיס" style="max-width: 100%; border-radius: 8px; margin: 12px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08);" />

**איפה מוצאים?**
- את הקישורים לטופס ולאתר: **טופס לידים**, בתפריט של עמוד **לקוחות**
- את התמונה, הצבעים והטקסטים של האתר: **הגדרות ← עיצוב המיניסייט**

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>📝 וגם:</strong> כל עדכון אוטומטי של כרטיס לקוח, משיחה, מדניאלה, מוובהוק או מבוט, נרשם עכשיו כהערה בכרטיס, עם מה שהשתנה. כך תמיד רואים מה עודכן ומאיפה.</div>

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/crm" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">📝 לטופס הלידים שלכם</span></a></div>`
  },
  {
    id: 55,
    areas: ["listings"],
    date: '14-10-2026',
    showOnlyForRealEstate: true,
    title: 'תזכורת לסיום חוזה שכירות',
    content: `כשמסמנים נכס להשכרה כ**הושכר** ב**ניהול נכסים**, אפשר עכשיו להזין את תאריכי חוזה השכירות. חודשיים וחודש לפני סיום החוזה תקבלו תזכורת בוואטסאפ, כדי לבדוק עם בעל הנכס אם מחדשים את החוזה או מפרסמים את הנכס מחדש.

הנכסים המושכרים מרוכזים בלשונית **הושכר**, עם הזמן שנותר לכל חוזה. את התזכורת אפשר לכבות ב**הגדרות**.`
  },
  {
    id: 54,
    areas: ["customers", "listings"],
    date: '11-10-2026',
    showOnlyForRealEstate: true,
    title: 'שטח בנוי בהתאמות ובסינון',
    content: `ה**שטח הבנוי** שמוגדר ללקוח משפיע עכשיו על ההתאמות: נכס שקטן בהרבה מהמינימום שהלקוח ביקש לא יוצע לו, נכס שקטן במעט יוצג עם סימון, והשטח נכנס לחישוב אחוז ההתאמה.

את השטח מגדירים בכרטיס הלקוח, ב**פרטי חיפוש**, בשבב **שטח**. סינון לפי שטח נוסף גם ל**לוח נכסים**, ל**התאמת נכסים**, ל**השכרה למכירה** ולחיפוש הנכסים שבכרטיס הלקוח.`
  },
  {
    id: 53,
    areas: ["signatures"],
    date: '07-10-2026',
    showOnlyForRealEstate: true,
    title: 'החתמת משכיר בבלעדיות',
    content: `ב**הכנת חוזה**, כשבוחרים **משכיר**, אפשר עכשיו לסמן **החתמה בבלעדיות**. המשכיר מקבל את הזמנת שירותי התיווך ואת מסמך הבלעדיות יחד, כמו בהחתמת מוכר.

את מסמך הבלעדיות למשכיר אפשר להחליף ב**הגדרות חתימות**, בשורה **החתמת משכיר בבלעדיות**.`
  },
  {
    id: 52,
    areas: ["customers"],
    date: '07-10-2026',
    title: 'ייבוא מוואטסאפ: אנשי קשר שמורים',
    content: `ב**ייבוא לקוחות** מוואטסאפ מוצגים עכשיו כברירת מחדל רק המספרים ששמורים באנשי הקשר בטלפון.

כדי לטעון גם מספרים שלא שמורים אצלכם, בוחרים **מורחב** מעל הטבלה. כדאי לעבור על המספרים לפני הייבוא.`
  },
  {
    id: 51,
    areas: ["meetings"],
    date: '07-10-2026',
    title: 'הודעה למוזמנים בפגישה',
    content: `בטופס הפגישה ב**יומן** נוסף שדה **הודעה למוזמנים**. מה שכותבים בו נשלח למוזמנים בוואטסאפ יחד עם הזימון, עם התזכורת ועם עדכון על שינוי מועד.

השדה שהיה קודם נקרא עכשיו **הערה פנימית**, ולא נשלח למוזמנים בוואטסאפ.`
  },
  {
    id: 50,
    areas: ["signatures"],
    date: '02-10-2026',
    showOnlyForRealEstate: true,
    title: 'מסמכי החתימה של כל המשרד',
    content: `מנהלי משרד רואים עכשיו את מסמכי החתימה של כל המתווכים במשרד, לצפייה בלבד.

ב**חתימות שנשלחו** בוחרים **כל המשרד**, ואפשר לסנן לפי מתווך.`
  },
  {
    id: 49,
    areas: ["listings"],
    date: '02-10-2026',
    showOnlyForRealEstate: true,
    title: 'ממ"ק ומקלט בנכסים שלכם',
    content: `ב**ניהול נכסים** אפשר עכשיו לסמן על נכס גם **ממ"ק** ו**מקלט**, לצד ממ"ד.

נכס שסומן כך יימצא בסינון **מרחב מוגן**, ויותאם גם לקונים שביקשו ממ"ק או מקלט. כשמוסיפים ל-Webot נכס מיד2, הסימון עובר איתו.`
  },
  {
    id: 48,
    areas: ["listings"],
    date: '29-09-2026',
    showOnlyForRealEstate: true,
    title: 'סינון התראות על נכסים',
    content: `בווידג׳ט **נכסים חדשים** בדף הבית אפשר עכשיו לבחור מה מוצג: הכל, רק נכסים **באזורים שלי**, או רק נכסים **שמתאימים ללקוחות שלי** — עם מספר הנכסים בכל תצוגה.

סוג העסקה וסוג המפרסם עברו לכפתור הסינון שליד כותרת הווידג׳ט.`
  },
  {
    id: 47,
    areas: ["signatures"],
    date: '29-09-2026',
    showOnlyForRealEstate: true,
    title: 'שיפורים בהחתמת לקוחות',
    content: `- אחרי **הכנת חוזה** בוחרים: שליחה ללקוח, העתקת קישור, החתמה במקום או PDF להדפסה. אין שליחה אוטומטית.
- **החתמה במקום** ו**הדפסה להחתמה ידנית** זמינות גם בפרטי החוזה. כשהלקוח לידכם ואתם מחוברים לחשבון שלכם, אפשר לדלג על קוד ה־SMS.
- שופר עורך התבניות בנייד ובמחשב, ותוקן חיתוך שורות ב־PDF.`
  },
  {
    id: 43,
    areas: ["signatures"],
    date: '24-09-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: הסכם שת״פ בין מתווכים',
    content: `### הסכם שת״פ בין מתווכים

תבנית Webot חדשה להסדרת שיתוף פעולה בין מתווכים זמינה עכשיו במרקטפלייס התבניות.

ממלאים את פרטי הנכס ושכר שיתוף הפעולה, ובוחרים בצורה ברורה מי מהמתווכים משלם למי.

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/signatures" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">לשליחת הסכם שת״פ</span></a></div>`
  },
  {
    id: 42,
    areas: ["listings","customers"],
    date: '18-09-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: התאמת נכס ללקוחות — שליחה להרבה לקוחות בבת אחת 🎯',
    content: `### התאמת נכס ללקוחות — שיפורים 🎯

שדרגנו את ההתאמה בין נכס ללקוחות: רואים בדיוק למה כל לקוח מתאים, ושולחים את הנכס להרבה לקוחות בבת אחת.

**מה חדש?**
- **טבלה אחת לכל ההתאמות** — הלקוחות שלכם ולקוחות המשרד (לפי הרשאות המשרד), מסודרים לפי אחוז ההתאמה
- **למה הלקוח מתאים** — התאמה **מלאה** או **חלקית**, והסיבות: תקציב, חדרים, אזור, ממ"ד ועוד
- **בחירה מרובה** — מסמנים לקוחות, או בוחרים בלחיצה את כל ההתאמות מעל אחוז מסוים
- **פעולות על כל הנבחרים** — הוספה למיניסייט, דיוור בתוסף, **הוספה לקהל** ו**שליחה בוואטסאפ הרשמי**: הנכס נוסף למיניסייט של כל לקוח, וכל לקוח מקבל קישור ישיר אליו (ללקוחות בבטא של הוואטסאפ הרשמי)
- **כרטיס הלקוח בלחיצה** — לחיצה על שורה פותחת את כרטיס הלקוח בצד, בלי לסגור את הנכס

**איך משתמשים?**
1. פותחים נכס ועוברים ללשונית **התאמת נכס לקונים**
2. מסמנים את הלקוחות שמתאימים לכם
3. בוחרים פעולה מהסרגל שמופיע מעל הטבלה

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/listings-board" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🎯 ללוח הנכסים</span></a></div>`
  },
  {
    id: 41,
    areas: ["ai"],
    date: '17-09-2026',
    showOnlyForRealEstate: true,
    title: 'מנוע AI חדש לקריאייטיבים  🎨',
    content: `### מנוע AI חדש לקריאייטיבים  🎨

החלפנו את מנוע ה-AI שמאחורי **יצירת הקריאייטיבים** — והשירות גם הוזל.

**מה חדש?**
- **עברית מדויקת יותר בקריאייטיבים** — פחות מילים כפולות, שגיאות כתיב ולוגואים משובשים
- **שיפור בשיפוץ וירטואלי** — מסדר ומנקה גם תמונות עמוסות, ושומר על החדר האמיתי
- **תמונות שבהן מופיעים אנשים** — המנוע הרגיל מטפל עכשיו גם בהן
- **המחיר ירד** — שיפוץ במנוע הרגיל עולה **1 מטבע** (במקום 3), ו-2 מטבעות ברזולוציה גבוהה

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 טוב לדעת:</strong> המנוע החדש יסודי יותר, ולכן כל תמונה לוקחת בדרך כלל כחצי דקה עד דקה.</div>`
  },
  {
    id: 40,
    areas: ["listings"],
    date: '13-09-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: נכס מהלוח הופך לנכס שלכם — עם בעל הנכס 🏷️',
    content: `### נכס מהלוח הופך לנכס שלכם — עם בעל הנכס 🏷️

מצאתם נכס מעניין בלוח הנכסים? מעכשיו אפשר לקחת אותו אליכם, **לתקן בו כל פרט** ולשמור עליו את בעל הנכס — בלי לצאת מהלוח.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> בשיחת בירור מגלים שהמרפסת היא מרפסת שירות ולא מרפסת שמש, או שהשטח לא מדויק? מתקנים — והנכס שלכם הוא זה שמופיע בלוח, עם הנתונים הנכונים. אלו בדיוק הנתונים שמזינים את הערכת השווי ואת ההתאמה לקונים.</div>

**מה חדש?**
- **תיקון כל פרט בנכס** — מוסיפים את הנכס אליכם ועורכים כל שדה. מהרגע הזה הלוח מציג **את העותק שלכם** במקום המודעה המקורית
- **בעל הנכס נשמר על הנכס** — בוחרים מהלקוחות שלכם, או פשוט מקלידים שם וטלפון. **לא חייבים ליצור כרטיס לקוח** — רוצים? מסמנים ✓ והוא נוצר ומקושר לנכס אוטומטית
- **אזהרה על מספר וירטואלי** — רוב המספרים בלוח הם מספרי יד2 מסווים. אנחנו מזהים ומתריעים לפני השמירה, כדי שלא יישמר לקוח שאי אפשר להתקשר אליו
- **פתיחה ועריכה ישר מהלוח** — לחיצה על נכס שלכם בלוח פותחת אותו לעריכה מלאה, בדיוק כמו מעמוד ניהול הנכסים
- **כרטיס בעל הנכס בלחיצה** — מתוך הנכס פותחים את כרטיס הלקוח המלא בצד, בלי לסגור את הנכס
- **קישור למודעות בלוחות** — מחפשים את המודעות של אותו נכס ביד2 ובמדלן דרך חלון חיפוש עם סינון, ומקשרים אותן לנכס שלכם

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 טוב לדעת:</strong> אחרי שמוסיפים נכס, העותק שלכם מופיע בלוח מיד. לוקח לנו עוד כדקה לסיים לעדכן את כל המערכת — עד אז מופיע סימון קטן על השורה.</div>

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/listings-board" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🏷️ ללוח הנכסים</span></a></div>`
  },
  {
    id: 39,
    areas: ["customers"],
    date: '11-09-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: עמוד הלקוחות החדש — פתוח לכולם 👥',
    content: `### עמוד הלקוחות החדש — פתוח לכולם 👥

בנינו מחדש את עמוד **הלקוחות** — מהיר יותר, נוח יותר, ועם כלים לעבודה של משרד שלם. עד עכשיו הוא היה פתוח לקבוצה קטנה של משתמשים, ומהיום הוא פתוח לכולם. העמוד הישן נשאר במרחק לחיצה.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> מגיעים ללקוח הנכון בפחות קליקים: חיפוש מהיר גם במאגר גדול, סינון לפי מה שחשוב לכם, ופעולות על הרבה לקוחות בבת אחת — במקום לעבור עליהם אחד־אחד.</div>

**מה חדש?**
- **חיפוש וסינון מהירים** — תוצאות מיידיות גם כשיש לכם אלפי לקוחות, סינונים מהירים בראש העמוד ו**סינון מתקדם** לכל השאר
- **עריכה ישירות בטבלה** — מעדכנים פרטים ישר מהשורה, בלי לפתוח חלון
- **פעולות על כמה לקוחות בבת אחת** — מסמנים לקוחות (או את כל תוצאות הסינון) ובוחרים: עריכה מרובה, הוספה לקהל, חסימת דיוור או ייצוא
- **כל הפרטים בפאנל צד** — לחיצה על לקוח פותחת את כרטיס הלקוח המלא בצד, בלי לאבד את המקום ברשימה
- **למשרדים** — **מאגר המשרד** מציג את הלקוחות של כל הצוות (לפי הרשאות המשרד), ומנהלי משרד משייכים לכל לקוח **מתווך מטפל**
- **נוח גם בנייד** — תצוגת כרטיסים שבנויה למסך הטלפון

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 טוב לדעת:</strong> העמוד הישן לא הולך לשום מקום. בעמוד הלקוחות הישן לוחצים על <strong>נסיון עמוד לקוחות חדש</strong>, ובעמוד החדש על <strong>חזרה לעמוד לקוחות ישן</strong> — והכניסה <strong>לקוחות</strong> בתפריט זוכרת באיזה עמוד השתמשתם לאחרונה. זו גרסת בטא: יש לכם הערות? ספרו לנו דרך הפס שבראש העמוד החדש.</div>

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/crm" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">👥 לעמוד הלקוחות החדש</span></a></div>`
  },
  {
    id: 38,
    areas: ["messaging"],
    date: '10-09-2026',
    title: 'חדש: שליחת הודעות וואטסאפ לקהל שלם 👥',
    content: `### שליחה לקהלים 👥

**קהלים** הם רשימות תפוצה שמורות — בונים אותם פעם אחת מלקוחות, מייבוא וואטסאפ או מקובץ, ומעכשיו אפשר גם **לשלוח אליהם הודעות** ישירות דרך מערכת הדיוור המוכרת.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> במקום לסמן את אותם לקוחות מחדש לפני כל דיוור — שומרים אותם פעם אחת כקהל (״משקיעים״, ״מתעניינים בצפון״), ושולחים לכולם בלחיצה אחת, כולל מספרים שאינם שמורים כלקוחות.</div>

**מה חדש?**
- **בחירת קהל בעמוד שליחת הדיוור** — בשלב בחירת הנמענים עוברים למצב ״קהל שמור״, בוחרים קהל וממשיכים ישר לכתיבת ההודעה. המערכת זוכרת את המצב האחרון שבחרתם
- **כפתור שליחה בעמוד הקהל** — פותח את חלון הדיוור עם כל חברי הקהל מסומנים
- **קהל מכל מקור** — בחירה מלקוחות, ייבוא מקבוצות וואטסאפ, או העלאת קובץ
- **עמודות מהקובץ הופכות לתבניות** — העליתם קובץ עם עמודות נוספות? כל עמודה זמינה כשדה אישי בהודעה (בחלון הדיוור)
- **כל ההגנות נשמרות** — רשימת החסומים לדיוור, סינון נמענים שקיבלו הודעה לאחרונה, והשהיות בין הודעות

**איך משתמשים?**
1. נכנסים לעמוד **שליחת דיוור ב-WhatsApp** כרגיל
2. בשלב בחירת הנמענים — בוחרים **קהל שמור** במקום לסמן שורות
3. כותבים את ההודעה — ושולחים כרגיל

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/whatsapp/audiences" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">👥 לקהלים שלי</span></a></div>`
  },
  {
    id: 37,
    areas: ["listings"],
    date: '27-08-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: קבוצות אזורים — כל האזורים שלכם בלחיצה אחת 🗂️',
    content: `### קבוצות אזורים 🗂️

שמרו רשימת אזורים עם שם משלה — ״צפון תל אביב״ בנפרד מ״הקריות״ — ובחרו את כולה בלחיצה אחת, בכל מקום שבו בוחרים אזורים.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> במקום לבחור שכונה־שכונה בכל חיפוש, בוחרים קבוצה אחת. אפשר לשמור כמה קבוצות ולעבור ביניהן לפי סוג החיפוש.</div>

**מה חדש?**
- **קבוצות אזורים** — עד 50 אזורים בקבוצה, עם שם וסמל משלה
- **עובד בכל מקום** — לוח הנכסים, התאמות, משכירות לרכישה, ובאזורים המבוקשים של הלקוח
- **נפתח על מה שאתם באמת משתמשים בו** — עוד לפני שמקלידים, הרשימה נפתחת על **האזורים שלי** ועל **הקבוצות** שלכם
- **ניהול מהיר** — גלגל השיניים שבתוך רשימת האזורים, או **הגדרות ← קבוצות אזורים**
- **וגם** — בבחירת רחוב רואים עכשיו גם את השכונות שהוא עובר בהן

<opengroups>🗂️ ליצירת הקבוצה הראשונה</opengroups>`
  },
  {
    id: 36,
    areas: ["listings"],
    date: '12-08-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: סטטיסטיקות חכמות בעמוד הנכסים 📊',
    content: `### סטטיסטיקות חכמות בעמוד הנכסים 📊

שדרגנו את אזור הנתונים בראש עמוד **ניהול נכסים** — מבט מודרני ומהיר על תיק הנכסים שלכם, וכפתור **"לכל הסטטיסטיקות"** שפותח מסך נתונים מלא.

<img src="/static/_haim/listings-stats-update.png" alt="סטטיסטיקות הנכסים החדשות" style="max-width: 100%; border-radius: 8px; margin: 12px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08);" />

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> כל המספרים שמנהלים איתם את העסק, במקום אחד: כמה נכסים פעילים, מה מוגן בבלעדיות ומה דורש חידוש, שווי התיק, פוטנציאל העמלות — והביצועים לאורך זמן עם בחירת טווח תאריכים.</div>

**מה חדש?**
- **מבט מהיר בראש העמוד** — נכסים פעילים, בלעדיות פעילה, שווי התיק ועמלות שמומשו, עם בחירת תקופה
- **התראות בלעדיות חכמות** — רואים מיד כמה בלעדיות מסתיימות תוך 30 יום, כמה **פגו על נכסים שעדיין בשיווק** (דורשים חידוש!) וכמה נכסים פעילים בלי הסכם בלעדיות בכלל
- **ביצועים לאורך זמן** — נכסים חדשים, עסקאות שנסגרו, עמלות וממוצע ימים לסגירה, עם גרף חודשי ובחירת טווח תאריכים חופשית
- **תצוגה מותאמת לתפקיד** — **מתווך** רואה את הנתונים על הנכסים שלו; **מנהלי משרד** רואים את כל המשרד, עם פירוט לכל מתווך בצוות
- **צלילה לכל מתווך** — למנהלים: לחיצה על מתווך פותחת את הנתונים שלו, גרף ביצועים ורשימת הנכסים שלו עם סינון ומיון

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💬 חסרה לכם סטטיסטיקה?</strong> אם יש נתון מסוים שהייתם רוצים לראות — ספרו לנו ונוסיף אותו. אנחנו בונים את המסך הזה בשבילכם.</div>

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/listings" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">📊 לצפייה בסטטיסטיקות בעמוד הנכסים</span></a></div>`
  },
  {
    id: 35,
    areas: ["customers"],
    date: '10-08-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: עמוד לקוח — הכל על הלקוח במסך אחד 👤',
    content: `### עמוד לקוח חדש — הכל על הלקוח במסך אחד 👤

מעכשיו **לכל לקוח יש עמוד משלו** — לחיצה על שם הלקוח ברשימה פותחת אותו מיד, עם כל מה שקשור אליו במקום אחד.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> כל ההיסטוריה מול הלקוח — שיחות עם סיכומי AI, פגישות, משימות, הערות, חתימות והנכסים שהצעתם — בעמוד אחד. מתקשרים, שולחים וואטסאפ, מעדכנים סטטוס וקובעים פגישה בלי לצאת מהעמוד.</div>

**מה חדש?**
- **פעולות מהירות בראש העמוד** — חיוג, וואטסאפ, עריכת פרטים, וכפתור **פעולות** לקביעת פגישה, פתיחת משימה, הוספת הערה או שליחת מסמך לחתימה
- **סטטוס בלחיצה אחת** — שינוי סטטוס הליד וסימון 🔥 **ליד חם** ישירות מהכותרת, עם סרגל התקדמות שמראה איפה הליד עומד בתהליך
- **סקירה** — הפרטים ופרופיל החיפוש, המשימה הבאה שממתינה, סיכום AI, הפעילות האחרונה והמיניסייט של הלקוח
- **ציר זמן** — כל האירועים מול הלקוח לפי הסדר, כולל ציטוטים מסיכומי השיחות ומי מהצוות ביצע כל פעולה
- **התאמות** — הנכסים שבמיניסייט של הלקוח עם נתוני צפיות ולייקים, והוספת נכסים חדשים מ**הנכסים שלי**, **נכסי המשרד** או **מהשוק**
- **שיחות** — כל השיחות מול הלקוח, סינון לנכנסות / יוצאות / לא נענו, האזנה להקלטה, תמלול מלא וסיכום AI
- **משימות ופגישות** — המשימות מסודרות לפי באיחור / היום / השבוע, וכל הפגישות הקרובות והעבר
- **חתימות ומסמכים** — מעקב אחרי כל חותם עם שליחת תזכורת, וארכיון קבצים אישי שאפשר להעלות אליו מסמכים (חוזים חתומים נשמרים אליו אוטומטית)
- **הערות ודיוור** — הערות עם **הקלטה קולית** 🎤 ונעיצה 📌, שיתוף הערה עם הצוות, וכל הודעות הדיוור שנשלחו ללקוח

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 טוב לדעת:</strong> העמוד בנוי גם לנייד — הפעולות מופיעות ככפתורים גדולים ונוחים ללחיצה. לחיצה על <strong>חזרה</strong> (או על מקש Esc) מחזירה אתכם לרשימה בדיוק איפה שהייתם, עם הסינון והגלילה שלכם. אפשר גם לשמור קישור ישיר לעמוד של לקוח מסוים ולחזור אליו מתי שתרצו.</div>

**איך פותחים?**
1. היכנסו ל**לקוחות**
2. לחצו על **שם הלקוח** ברשימה
3. עמוד הלקוח נפתח — עברו בין הלשוניות שבראש העמוד

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/contacts" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">👤 לפתיחת עמוד לקוח</span></a></div>`
  },
  {
    id: 34,
    areas: ["messaging"],
    date: '29-07-2026',
    title: 'חדש: תיוג וניהול המספרים הווירטואליים 🏷️📞',
    content: `### תיוג וניהול המספרים הווירטואליים 🏷️📞

יש לכם כמה מספרים וירטואליים לקמפיינים שונים? מעכשיו תוכלו **לתת לכל מספר תגית** שמזהה את המקור שלו — ולראות אותה ישירות בעמוד השיחות.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> במקום לזכור איזה מספר שייך לאיזה קמפיין, פשוט תייגו: "מודעת יד2", "שלט חוצות", "קמפיין פייסבוק" — והתגית תופיע ליד המספר בסינון השיחות. קל לזהות מאיפה הגיעה כל שיחה.</div>

**מה חדש?**
- **תגית לכל מספר** — תנו שם ידידותי לכל מספר וירטואלי לפי המקור שלו
- **התגית בעמוד השיחות** — היא מופיעה בסינון לפי מספר ובעמודת הבעלים, ואפשר גם לחפש לפיה
- **ניהול ישיר מעמוד השיחות** — לחצו על גלגל השיניים ⚙️ שליד כותרת העמוד כדי לערוך את המספרים בלי לצאת מהעמוד
- **הודעת מענה אוטומטי** — עדכנו את הודעת הווטסאפ שנשלחת למי שהתקשר ולא עניתם, ישירות מאותו מסך

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 טוב לדעת:</strong> הכל נשמר אוטומטית — פשוט הקלידו תגית והיא נשמרת. אפשר לנהל את המספרים גם מתוך <strong>הגדרות</strong>.</div>

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/newCalls" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">📞 לתיוג המספרים בעמוד השיחות</span></a></div>`
  },
  {
    id: 34,
    areas: ["listings"],
    date: '23-07-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: תזכורות לפני שהבלעדיות נגמרת ⏳',
    content: `### תזכורות לפני שהבלעדיות נגמרת ⏳

בלעדיות שנגמרת בלי ששמתם לב זה נכס שהולך למתווך אחר. מעכשיו Webot שומרת לכם על התאריכים: **שבועיים ושבוע** לפני שהבלעדיות על נכס שלכם מסתיימת — תקבלו תזכורת בוואטסאפ.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> מספיק זמן לחדש את הבלעדיות מול הבעלים או לסגור עסקה — במקום לגלות בדיעבד שהנכס כבר מפורסם אצל מישהו אחר.</div>

**מה חדש?**
- **תזכורת בוואטסאפ פעמיים** — שבועיים לפני ושוב שבוע לפני סיום הבלעדיות, עם הכתובת, התאריך המדויק וקישור ישיר לנכס
- **כרטיס התראה בדף הבית** — מופיע שבועיים לפני הסיום ונשאר עד היום האחרון, עם תמונת הנכס וכמה ימים נשארו
- **למנהלי משרד** — הכרטיס מציג את הנכסים של **כל המתווכים במשרד**, עם התמונה והשם של המתווך האחראי

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 טוב לדעת:</strong> התזכורות נשלחות רק על נכסים בסטטוס <strong>למכירה</strong> או <strong>בתהליך</strong> — נכס שנמכר או הוסר לא יטריד אתכם. הכל עובד אוטומטית, רק צריך שתאריך הבלעדיות יהיה מעודכן בכרטיס הנכס.</div>

**איך מוודאים שזה עובד?**
1. היכנסו ל**נכסים** ופתחו נכס שיש עליו בלעדיות
2. ודאו ששדה **תאריך סיום בלעדיות** מעודכן
3. זהו — אנחנו נזכיר לכם בזמן

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/listings" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">⏳ לבדיקת תאריכי הבלעדיות שלכם</span></a></div>

<div style="font-size:13px;color:#637381;margin-top:8px;">לא מעוניינים בתזכורות? אפשר לכבות אותן ב<strong>הגדרות</strong>, במקטע ההתראות.</div>`
  },
  {
    id: 33,
    areas: ["account"],
    date: '19-07-2026',
    title: 'חדש: התחברות עם אימייל וסיסמה 🔐',
    content: `### התחברות עם אימייל וסיסמה 🔐

עד היום ההתחברות ל-Webot הייתה דרך חשבון Google בלבד. מעכשיו תוכלו להתחבר גם עם **אימייל וסיסמה** — איך שנוח לכם.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> כבר לא חייבים חשבון Google כדי להיכנס. אפשר להגדיר סיסמה אישית ולהתחבר איתה מכל מכשיר.</div>

**מה חדש?**
- **הרשמה עצמאית** — משתמשים חדשים יכולים לפתוח חשבון עם אימייל וסיסמה (עם אימות כתובת האימייל)
- **שכחתי סיסמה** — שחזור סיסמה בלחיצה, ישירות ממסך ההתחברות
- **הגדרת סיסמה למשתמשי Google** — אם אתם מחוברים עם Google, אפשר עכשיו להגדיר סיסמה בהגדרות ולהתחבר בשתי הדרכים

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 טוב לדעת:</strong> אם אתם כבר מחוברים עם Google — לא צריך לשנות כלום. פשוט אפשר עכשיו, אם בא לכם, להגדיר גם סיסמה.</div>

**איך מגדירים סיסמה?**
1. היכנסו ל**הגדרות**
2. במקטע **אבטחה**, הזינו סיסמה חדשה ואשרו אותה
3. מעכשיו תוכלו להתחבר גם עם האימייל והסיסמה שלכם

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/settings/security" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🔐 להגדרת סיסמה בהגדרות</span></a></div>`
  },
  {
    id: 32,
    areas: ["minisites"],
    date: '15-07-2026',
    showOnlyForRealEstate: true,
    title: 'המיניסייט שלכם קיבל מראה חדש 🏠✨',
    content: `### המיניסייט שלכם קיבל מראה חדש 🏠✨

עיצבנו מחדש את **המיניסייטים הציבוריים** שלכם — גם את דף הנכסים האישי וגם את המיניסייטים שאתם שולחים ללקוחות. עכשיו הם נראים כמו אתר אמיתי שלכם: כותרת עליונה קבועה עם פרטי הקשר, אזור פתיחה מרשים עם התמונה שלכם, ופוטר מקצועי בצבעי המותג.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> דף שתתגאו לשלוח ללקוחות — עם הסיפור שלכם, הרשתות החברתיות שלכם והמיתוג שלכם. כל הקישורים הקיימים ממשיכים לעבוד — העיצוב החדש כבר פעיל.</div>

**מה חדש?**
- **משפט תדמית** — שורה אחת חזקה מתחת לשם שלכם (למשל: "המתווך מספר 1 באזור חדרה והסביבה")
- **מקטע "אודות" מעוצב** — ספרו על עצמכם טיפה יותר 
- **אינסטגרם ופייסבוק** — הלקוחות יגיעו לרשתות שלכם ישירות מהמיניסייט
- **כתובת המשרד** — מוצגת בפוטר וליד פרטי הקשר (אופציונלי)
- **רקע לכותרת** — בחרו דוגמת רקע עדינה (נקודות, גלים, רשת...) או העלו תמונת רקע משלכם
- **פוטר בצבעי המותג** — גוון כהה של צבע הנושא שבחרתם, עם כל פרטי הקשר במקום אחד

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 טוב לדעת:</strong> הטקסט שכבר כתבתם על עצמכם עבר אוטומטית למקום הנכון — אבל שווה להיכנס להגדרות, להשלים את הפרטים החדשים ולראות איך המיניסייט שלכם נראה עכשיו.</div>

**איך מעצבים?**
1. היכנסו ל**הגדרות** ← **"עיצוב המיניסייט"**
2. הוסיפו משפט תדמית, טקסט אודות, קישורים לרשתות וכתובת
3. בחרו צבע, דוגמת רקע או תמונה — ותראו הכל בתצוגה מקדימה חיה

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/settings/minisite" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🎨 לעיצוב המיניסייט בהגדרות</span></a></div>`
  },
  {
    id: 31,
    areas: ["listings"],
    date: '12-07-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: התראות על נכסים להשכרה 🔑',
    content: `### התראות על נכסים להשכרה 🔑

עד היום קיבלתם התראות רק על נכסים **למכירה**. מעכשיו תוכלו לקבל התראות גם על נכסים **להשכרה** — בדיוק כמו שאתם רגילים: נכסים חדשים שעולים לשוק וגם ירידות מחיר.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> אם אתם עובדים גם עם שוכרים — תדעו ראשונים על כל דירה חדשה להשכרה באזורים שלכם, ותוכלו להתאים אותה מיד לשוכרים המתאימים מרשימת הלקוחות שלכם.</div>

**מה חדש?**
- **התראות על נכסים חדשים להשכרה** — כמו בהתראות למכירה, על כל דירה חדשה שעולה לשוק להשכרה באזורי הפעילות שלכם
- **התראות על ירידות מחיר בשכירות** — גם על נכסים להשכרה שהמחיר שלהם ירד
- **התאמה חכמה לשוכרים** — נכס להשכרה מותאם אוטומטית ללקוחות מסוג **שוכר**, לפי תקציב השכירות שלהם
- **סינון בדף הראשי** — בווידג'טים של ההתראות וההתאמות בדף הבית תוכלו לסנן בין **מכירה** ל**השכרה**

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 חשוב לדעת:</strong> כברירת מחדל ההתראות ממשיכות להישלח על נכסים <strong>למכירה בלבד</strong>. כדי לקבל גם התראות על שכירות — הפעילו זאת בהגדרות.</div>

**איך מפעילים?**
1. היכנסו ל**הגדרות**
2. במקטע ההתראות, בחרו על אילו נכסים לקבל התראות — **למכירה**, **להשכרה**, או שניהם
3. זהו — מאותו רגע תתחילו לקבל התראות גם על נכסים להשכרה

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/settings/listingAlerts" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🔑 להפעלה בהגדרות</span></a></div>`
  },
  {
    id: 30,
    areas: ["ai","listings"],
    date: '08-07-2026',
    showOnlyForRealEstate: true,
    title: 'חדש: הסרת סימני מים מתמונות נכסים 🪄',
    content: `### הסרת סימני מים מתמונות נכסים 🪄

כלי חדש שמסיר בעזרת AI **סימני מים** — וגם לוגואים, טלפונים, באנרים ותמונות מתווך שמודבקים על תמונות הנכס — ומשאיר תמונה נקייה ומקצועית.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה זה נותן לכם?</strong> תמונות נכס נקיות לשימוש במיניסייט ללקוחות שלכם — בלי מיתוג של פורטלים ובלי פרטי מתווכים אחרים. לחיצה אחת מנקה את כל תמונות הנכס — <strong>ובחינם</strong>.</div>

**לפני ואחרי:**

<div>
<beforeafter before="/static/watermarks/demo-before.png" after="/static/watermarks/demo-after.png"></beforeafter>
</div>

**איך משתמשים?**
1. בכרטיס נכס (בהתאמת נכסים או בלוח הנכסים) — פתחו את תפריט שלוש הנקודות ובחרו **"הסרת סימני מים"**
2. לחצו **"הסר סימני מים מכל התמונות"** — או נקו תמונה-תמונה
3. לחצו על כל תמונה כדי לראות **השוואת לפני / אחרי** בגדול, עם מחוון גרירה
4. התוצאה לא מושלמת? כפתור **"לא יצא טוב? נסו שוב"** מייצר גרסה חדשה

**ובמיניסייט:**
- מתג חדש **"תמונות ללא סימני מים"** בעריכת המיניסייט מציג ללקוח את הגרסאות הנקיות
- ההעדפה נשמרת אוטומטית גם למיניסייטים הבאים שלכם
- בחלון בחירת התמונות של המיניסייט תראו בדיוק כמה תמונות כבר נקיות

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 טוב לדעת:</strong> תמונה שנוקתה נשמרת על הנכס — אם מישהו במשרד כבר ניקה אותה, תקבלו את התוצאה מיידית ובחינם.</div>

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/listingsMatch" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🪄 נסו עכשיו בהתאמת נכסים</span></a></div>`
  },
  {
    id: 29,
    areas: ["ai"],
    date: '30-06-2026',
    showOnlyForRealEstate: true,
    title: 'הקשבנו לכם — הוספנו דרך זולה יותר לעיצובי AI',
    content: `### דרך חדשה וזולה יותר לעיצובי AI

הקשבנו לכם! ביקשתם דרך משתלמת יותר לייצר עיצובי AI לתמונות הנכס — אז בדקנו, השווינו, והוספנו **מנועים זולים יותר** לבחירתכם.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה חדש?</strong> במסך יצירת העיצוב אפשר עכשיו לבחור את <strong>רמת המנוע</strong> (חסכוני / רגיל / איכות מלאה) ואת <strong>הרזולוציה</strong> — וכל אפשרות מתומחרת בנפרד. המנוע החסכוני עולה עד פי כמה פחות.</div>

**איך בוחרים?**

- **מנוע חזק יותר** — תוצאה מציאותית, מלוטשת ודרמטית יותר, אך עולה יותר טוקנים.
- **מנוע חסכוני** — זול משמעותית ונוטה לשמור יותר על המקור. לפעמים התוצאה פחות טובה — אך ברוב המקרים בהחלט טובה מספיק.

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>💡 הטיפ שלנו: כדאי לנסות ולהשוות.</strong> התוצאה משתנה מתמונה לתמונה. לעיתים עדיף לנסות פעם-פעמיים עם מנוע חסכוני — וזה עדיין יוצא זול — מאשר פעם אחת עם מנוע יקר.</div>

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎁 ויש עוד —</strong> החל מחודש יולי הגדלנו את מכסת הטוקנים החודשית שאתם מקבלים. יותר טוקנים בכל חודש, כדי שתוכלו ליצור עוד.</div>

לחצו על סימן השאלה (?) שליד "רמת המנוע והאיכות" במסך העיצוב כדי לקרוא עוד על ההבדלים.

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/makeovers" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🎨 פתיחת עיצובי AI</span></a></div>`
  },
  {
    id: 27,
    areas: ["ai"],
    date: '22-06-2026',
    showOnlyForRealEstate: true,
    title: 'טוקנים חדשים — הוזלנו משמעותית את מחיר הקריאטיבים 🎉',
    content: `### טוקנים — דרך חדשה ומשתלמת ליצור קריאטיבים 🪙

הוזלנו **משמעותית** את מחיר יצירת הקריאטיבים, כדי שתוכלו ליצור הרבה יותר — בהרבה פחות.

<div style="background:#E7F6EC;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>🎉 מה השתנה?</strong> עברנו למודל טוקנים: כל קריאטיב עולה כמות טוקנים קבועה, ואת הטוקנים רוכשים בחבילות משתלמות. ככל שהחבילה גדולה יותר — המחיר לקריאטיב יורד עוד.</div>

**למה זה טוב לכם?**

- **מחיר נמוך בהרבה לקריאטיב** — חבילות הטוקנים מוזילות את עלות הקריאטיב בעשרות אחוזים לעומת קודם.
- **משלמים רק על מה שיוצרים** — כל קריאטיב עולה כמות טוקנים קבועה, בלי הפתעות ובלי מכסה חודשית שנעלמת.
- **חבילות לכל היקף** — מ-Starter ועד Power, עם הנחה שגדלה ככל שהחבילה גדולה יותר (עד חיסכון משמעותי לטוקן).
- **טוקנים ברמת המשרד** — אפשר לרכוש טוקנים ברמת המשרד ולשתף אותם עם כל היוזרים במשרד — מאגר אחד משותף לכל הצוות.
- **טוקנים חודשיים למנויים** — ממשיכים לקבל מכסת טוקנים חודשית, ואפשר תמיד להוסיף עוד.
- **טוקנים שנרכשו לא פגים** — מה שרכשתם נשאר אצלכם, לתמיד.

לחצו על סימן השאלה שליד יתרת הטוקנים בעמוד הקריאטיבים כדי לקרוא עוד על אופן השימוש.

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/creatives-generator" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🎨 מעבר למחולל הקריאטיבים</span></a></div>`
  },
  {
    id: 28,
    areas: ["ai"],
    date: '22-06-2026',
    showOnlyForRealEstate: true,
    title: 'עיצובי AI לתמונות הנכס — נאמנים למבנה האמיתי',
    content: `### עיצובי AI לתמונות הנכס 🎨

כלי חדש שמעצב מחדש את תמונות הנכס בעזרת AI — ושומר **בנאמנות מלאה על המבנה האמיתי של הבית**. אותם קירות, אותם חלונות, אותה זווית צילום — רק העיצוב, הריהוט והאווירה משתנים.

<div style="background:#EDE7F6;border-right:4px solid #7C4DFF;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>⭐ נאמנים למציאות</strong> — אנחנו לא ממציאים חדרים, חלונות או חללים חדשים. התוצאה תמיד נשארת אמינה ומזוהה כאותו נכס — רק בגרסה מעוצבת ומזמינה יותר.</div>

**לפני ואחרי:**

<div>
<beforeafter before="/static/makeovers/demo-before.png" after="/static/makeovers/demo-after.png"></beforeafter>
</div>

<div>
<beforeafter before="/static/makeovers/demo4-before.png" after="/static/makeovers/demo4-after.png"></beforeafter>
</div>

<div>
<beforeafter before="/static/makeovers/demo3-before.png" after="/static/makeovers/demo3-after.png"></beforeafter>
</div>


### אז איך זה בא לעזור לכם?🧹

מעבר ל"מייקאובר" מלא, אפשר להשתמש בכלי גם למשימות פשוטות שמשדרגות תמונה בכמה שניות — בלי לשנות את הנכס עצמו:

- **סידור וניקיון הבית** — פינוי בלאגן, הסרת חפצים מיותרים והשארת חלל נקי ומסודר
- **סטיילינג קל** — הוספת נגיעות עיצוב, תאורה ואווירה חמה
- **עיצוב מלא** — גרסה מחודשת ומזמינה לחדר, לדירה או לגינה

**איך זה עובד?**
1. בוחרים נכס ועד **5 תמונות** לעיצוב
2. בוחרים סגנון מוכן (מודרני, סקנדינבי, יוקרתי ועוד) — או מתארים סגנון משלכם
3. מקבלים גרסאות מעוצבות, עם **השוואת לפני/אחרי** והורדה בלחיצה
4. התמונות שתבחרו יוצגו **אוטומטית במיני-סייט של הנכס**, בקטע "תמונות שעוצבו ב-AI"

<div style="background:#E8F5E9;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;">💡 כל עיצוב תמונה עולה טוקן אחד בלבד מהמאגר שלכם.</div>

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/makeovers" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🎨 פתיחת עיצובי AI</span></a></div>`
  },
  {
    id: 26,
    areas: ["listings"],
    date: '27-05-2026',
    showOnlyForRealEstate: true,
    title: 'לוח נכסים — מרכז חדש לסקירה והתאמה',
    content: `### לוח נכסים 🏘️

מקום חדש ומרכזי בו תוכלו לסקור בקלות **את כל הנכסים מכל המקורות** — Yad2, מדלן והנכסים שלכם ב-Webot — בעמוד אחד, מסונן לאזורי הפעילות שלכם.

<div style="background:#E8F5E9;border-right:4px solid #4CAF50;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>⭐ למה הוספנו את זה?</strong> כדי שתוכלו לפתוח עמוד אחד בבוקר ולראות מבט מהיר על כל הנכסים החדשים באזורים שלכם — בלי לנווט בין מקורות, ובלי לפתוח כל לקוח בנפרד.</div>

**מה אפשר לעשות שם?**

- **הערות אישיות על נכס** — הוסיפו הערות פרטיות שגלויות רק לכם. סימון ירוק על השורה מזכיר לכם בעתיד שיש לכם רישום על הנכס הזה.
- **לראות אם מישהו אחר במשרד כבר על הנכס** — אם מתווך אחר במשרד תיאם פגישה על נכס, תראו אינדיקציה מיידית בשורה — לפני שתרימו טלפון ללקוח שכבר נפגש איתו.
- **התאמה ללקוחות מתאימים** — בלחיצה אחת על נכס תקבלו את רשימת הלקוחות שלכם שמתאימים לו, בלי לחפש ידנית.
- **פינים והסתרות** — הצמידו נכסים מעניינים למעלה, או הסתירו נכסים שכבר טיפלתם בהם.
- **רענון אוטומטי** — בחרו רענון כל 15 / 30 / 60 דקות, או רעננו ידנית בכל רגע. השעון הקטן מתחת לכפתור מראה כמה זמן עד הרענון הבא.
- **באזורי הפעילות שלי** — לחיצה אחת מזינה את כל האזורים שהגדרתם בהגדרות הפעילות שלכם.
- **שתי תצוגות** — טבלה לסקירה מהירה, או כרטיסים עם תמונה גדולה לחיפוש ויזואלי.
- **פעולות מהירות לכל נכס** — הוספה לנכסים האישיים שלכם ב-Webot, יצירת משימה או פגישה, יצירת קריאייטב לפרסום, חיפוש קונים פוטנציאליים.

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/listings-board" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">🏘️ פתיחת לוח נכסים</span></a></div>`
  },
  {
    id: 25,
    areas: ["messaging"],
    date: '25-04-2026',
    title: 'שיפורים בלוג הדיוורים',
    content: `### שיפורים בלוג הדיוורים 📋

עדכון משמעותי לעמוד **לוג דיוורים** — חיפוש מהיר, גישה ישירה לכרטיס הלקוח, ופעולות חדשות לניהול הנמענים שלכם.

<div style="background:#FFF8E1;border-right:4px solid #F59E0B;padding:10px 14px;border-radius:8px;margin:16px 0;font-size:15px;"><strong>⭐ החידוש המרכזי — סימון לקוחות שביקשו לא לקבל יותר דיוורים</strong></div>

מעכשיו אפשר לסמן ישירות מתוך לוג הדיוורים לקוחות שביקשו לא לקבל עוד דיוורים — בלי לחפש אותם ידנית ברשימת הלקוחות.

- **תפריט פעולות לכל הודעה** — לחיצה על שלוש הנקודות בשורה פותחת את האפשרות "סמן שהלקוח לא מעוניין בדיוורים"
- **בחירה מרובה** — סמנו מספר נמענים בבת אחת באמצעות תיבות הסימון, ובצעו את הפעולה על כולם יחד מסרגל הפעולות שמופיע למעלה
- **יצירה אוטומטית של לקוח** — אם הנמען עדיין לא קיים אצלכם ברשימת הלקוחות, ייווצר עבורו כרטיס לקוח חדש עם רצינות לקוח **"אין התאמה"** ועם דגל ביטול דיוורים פעיל

מאותו רגע, הלקוח לא יקבל יותר דיוורים מכם — אוטומטית, בלי שתצטרכו לזכור.

### מה עוד חדש?

- **חיפוש לפי מספר טלפון** — חיפוש בכל ההיסטוריה לפי טלפון, בפורמט מקומי (052-543) או בינלאומי (+972 52 543...) — שניהם עובדים
- **לחיצה על שם הנמען פותחת את כרטיס הלקוח המלא** — כל המידע, ההיסטוריה, ההודעות שנשלחו, השיחות והפגישות. כפתור "עריכה" בכרטיס מאפשר לעדכן פרטים
- **הוספת לקוח ישירות מהלוג** — אם הנמען עדיין לא ברשימת הלקוחות, ייפתח חלון "עדיין לא לקוח" עם כפתור הוספה שמזין את הטלפון אוטומטית
- **בוחרי תאריכים חדשים** — קיצורים מהירים לטווחים נפוצים (היום, 7 ימים אחרונים, 30 ימים, החודש, חודש שעבר) ובחירה מותאמת
- **שם הלקוח העדכני בכל מקום** — שם הנמען מתעדכן אוטומטית מרשימת הלקוחות שלכם, גם אם בעת השליחה הוא עדיין לא היה שמור אצלכם בשם

<div style="text-align:center;margin:24px 0 8px;"><a href="/dashboard/whatsAppWeb/sendMessages" style="display:inline-block;background:#CDDC39;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:15px;"><span style="color:#212121;">📋 פתיחת לוג הדיוורים</span></a></div>`
  },
  {
    id: 24,
    areas: ["meetings"],
    date: '20-04-2026',
    showOnlyForRealEstate: true,
    title: 'פגישות קודמות עם מתווכים במשרד',
    content: `### פגישות קודמות עם מתווכים במשרד 🤝

<img src="/static/_haim/meetings-warning.png" alt="meetings warning" style="max-width: 100%; border-radius: 8px; margin-bottom: 16px;" />

בטופס יצירת פגישה או משימה חדשה, מופיע כעת כרטיס מידע שמראה אם הלקוח כבר נפגש בעבר עם מתווכים אחרים במשרד שלכם — בנוסף לאזהרת הפגישות בטווח של שבועיים סביב מועד הפגישה.

כך תוכלו לראות במבט אחד אם הלקוח אי פעם נפגש עם מישהו מהמשרד — לפני שאתם יוצרים איתו פגישה או משימה חדשה.

**מה חדש:**
- כרטיס מידע יחיד שמציג את כל המתווכים במשרד שכבר נפגשו עם הלקוח בעבר
- מציג את שם המתווך, מספר הפגישות ותאריך הפגישה האחרונה
- מבוסס על כל היסטוריית הפגישות, ולא רק על טווח השבועיים סביב הפגישה החדשה
- מופיע גם ביצירת פגישה וגם ביצירת משימה

**איך משתמשים?**
1. פיתחו פגישה או משימה חדשה ביומן
2. בחרו לקוח כמשתתף
3. כרטיס המידע יופיע אוטומטית אם הלקוח נפגש בעבר עם מתווך אחר במשרד`
  },
  {
    id: 23,
    areas: ["listings"],
    date: '15-04-2026',
    showOnlyForRealEstate: true,
    title: 'השכרה למכירה',
    content: `### השכרה למכירה 🏠➡️💰

כלי חדש שמאפשר לכם למצוא בעלי נכסים פרטיים שפרסמו את הנכס שלהם **להשכרה**, וליצור איתם קשר כדי לבדוק אם הם מעוניינים גם **למכור**.

**מה חדש:**
- חיפוש נכסים להשכרה של בעלי נכסים פרטיים (לא סוכנים) מ-Yad2 ו-Madlan
- סינון לפי מיקום, חדרים, מחיר, סוג נכס ועוד
- טבלה מרוכזת עם פרטי המשכיר בולטים — שם וטלפון
- בדיקה אוטומטית אם המשכיר כבר ברשימת הלקוחות שלכם
- בדיקה אוטומטית אם שלחתם למשכיר הודעה ב-30 הימים האחרונים
- אפשרות לבחור מספר משכירים ולשלוח הודעת WhatsApp מרוכזת
- אפשרות להוסיף משכירים לרשימת הלקוחות שלכם בלחיצה אחת

**איך משתמשים?**
1. היכנסו לעמוד **השכרה למכירה** בתפריט הצד
2. בחרו מיקום ולחצו חיפוש
3. סמנו משכירים שמעניינים אתכם
4. לחצו "שליחת הודעה ב-WhatsApp" או "הוספה ללקוחות"

<img src="/static/_haim/rental-to-sale-1.png" alt="השכרה למכירה — מסך ראשי" style="max-width:100%;border-radius:8px;margin:8px 0" />

<img src="/static/_haim/rental-to-sale-2.png" alt="השכרה למכירה — תוצאות חיפוש" style="max-width:100%;border-radius:8px;margin:8px 0" />`
  },
  {
    id: 22,
    areas: ["customers"],
    date: '15-04-2026',
    title: 'הערות מהירות על לקוחות',
    content: `### הערות מהירות על לקוחות 📝

אפשרות חדשה להוסיף הערות אישיות על כל לקוח — הערות שגלויות רק לכם ולא משותפות עם המשרד.

**מה חדש:**
- **אייקון הערות בטבלת הלקוחות** — ליד תפריט הפעולות של כל לקוח מופיע אייקון הערות. אם אין הערות — האייקון אפור. אם יש הערות — האייקון ירוק
- **פופאפ מהיר** — לחיצה על האייקון פותחת חלון קטן שמאפשר לצפות, לערוך ולהוסיף הערות בקלות
- **מספר הערות עם חותמת זמן** — כל הערה נשמרת עם תאריך ושעה, כך שתמיד תדעו מתי כתבתם אותה
- **עריכה ומחיקה** — לחצו על הערה כדי לערוך אותה, או העבירו את העכבר כדי לראות כפתור מחיקה
- **הערות בכרטיס הלקוח** — ההערות מופיעות גם בכרטיס הלקוח המורחב, מעל התגובות המשותפות של המשרד

**איך משתמשים?**
1. בטבלת הלקוחות, לחצו על אייקון ההערות ליד תפריט הפעולות
2. כתבו הערה ולחצו Enter או על כפתור ה-+
3. ההערה נשמרת מיידית ומסומנת בחותמת זמן

<img src="/static/_haim/quick-contact-notes.png" alt="הערות מהירות על לקוחות" style="max-width:100%;border-radius:8px;margin:8px 0" />`
  },
  {
    id: 21,
    areas: ["messaging"],
    date: '15-04-2026',
    title: 'שדרוג דיוור WhatsApp — תצוגת פרמטרים חכמה',
    content: `### שדרוג דיוור WhatsApp 💬

**מה חדש:**
- **תצוגת פרמטרים צבעונית** — בשלב השליחה, כל פרמטר שמשתמשים בו בהודעה מוצג בצ'יפ צבעוני. ליד כל לקוח תראו את הערכים בפועל שיוחלפו בהודעה
- **זיהוי פרמטרים חסרים** — אם ללקוח חסר ערך לפרמטר, תראו תווית אדומה עם שם הפרמטר החסר (למשל "⚠️ חסר שכונת הנכס ⚠️")
- **סינון חסרים בלבד** — כפתור חדש "הצג חסרים בלבד" מאפשר לראות רק את ההודעות עם פרמטרים חסרים, כדי לתקן אותן לפני השליחה
- **הרחבה/כיווץ** — אפשר להרחיב או לכווץ את כל ההודעות בלחיצה אחת
- **מסך מוגדל** — כפתור הגדלה חדש ליד כפתור הסגירה מאפשר לעבוד במסך רחב ומלא

**איך משתמשים?**
1. כתבו הודעה עם פרמטרים (למשל {{שם פרטי}}, {{עיר}})
2. עברו לשלב השליחה — תראו את הערכים בפועל ליד כל לקוח
3. אם יש חסרים, לחצו "הצג חסרים בלבד" כדי לתקן ידנית

<img src="/static/_haim/bulk-message-params-preview.png" alt="תצוגת פרמטרים בדיוור" style="max-width:100%;border-radius:8px;margin:8px 0" />`
  },
  {
    id: 20,
    areas: ["listings"],
    date: '12-04-2026',
    showOnlyForRealEstate: true,
    title: 'התאמת נכסים להשכרה',
    content: `### התאמת נכסים להשכרה 🏠

**מה חדש:**
- עכשיו אפשר לחפש ולהתאים גם **נכסים להשכרה** בעמוד **התאמת נכסים**
- כשבוחרים לקוח מסוג שוכר, המערכת עוברת אוטומטית לחיפוש נכסים להשכרה

**איך משתמשים?**
1. היכנסו ל**התאמת נכסים**
2. לחצו על כפתור **להשכרה** בסרגל החיפוש
3. או בחרו לקוח מסוג שוכר — והמערכת תעבור לשכירות אוטומטית`
  },
  {
    id: 19,
    areas: ["meetings"],
    date: '07-04-2026',
    showOnlyForRealEstate: true,
    title: 'התראת תיאום פגישות במשרד',
    content: `### התראת תיאום פגישות במשרד 🤝

**מה חדש:**
- כשאתם יוצרים פגישה או משימה חדשה ביומן, המערכת בודקת אוטומטית אם ללקוח כבר יש פגישות או משימות עם סוכנים אחרים במשרד שלכם
- אם נמצאה פגישה או משימה בטווח של שבועיים מהתאריך שבחרתם, תוצג התראה עם שם הסוכן והתאריך
- ההתראה מוצגת מעל כפתור היצירה, כדי שתוכלו לתאם עם הסוכן לפני קביעת פגישה נוספת

**איך משתמשים?**
1. היכנסו ל**יומן** וצרו פגישה או משימה חדשה
2. הוסיפו לקוח כמשתתף
3. אם יש פגישה או משימה קיימת עם סוכן אחר — תראו התראה צהובה עם הפרטים`
  },
  {
    id: 18,
    areas: ["minisites"],
    date: '31-03-2026',
    showOnlyForRealEstate: true,
    title: 'שדרוג המיניסייט ללקוחות',
    content: `### שדרוג המיניסייט ללקוחות 🏠

**מה חדש:**
- **פילטרים חכמים** — הלקוחות שלכם יכולים עכשיו לסנן נכסים לפי חדרים, שכונות, ולסדר לפי מחיר או חדרים. בנוסף, כפתורי "אהבתי" ו"לא אהבתי" מאפשרים ללקוח לראות במהירות רק את מה שמעניין אותו

<img src="/static/_haim/minisite filters screenshot.png" alt="פילטרים במיניסייט" style="max-width:100%;border-radius:8px;margin:8px 0" />

- **נכסים שלא אהבתי** — נכסים שהלקוח לא אהב מוסתרים מהרשימה הראשית ומוצגים באזור נפרד מתקפל
- **כפתור שיתוף** — הלקוח יכול לשתף נכס ספציפי ישירות מהכרטיס
- **סרגל פרטי קשר נצמד** — כשהלקוח גולל למטה, סרגל קומפקטי עם הפרטים שלכם נשאר למעלה


- **עיצוב מותאם אישית** — כנסו להגדרות ובחרו צבע רקע, צבע טקסט, ביו קצר ועוד. הלקוחות שלכם יראו מיניסייט מעוצב בצבעים שלכם
<img src="/static/_haim/edit minisite settings screenshot.png" alt="הגדרות עיצוב מיניסייט" style="max-width:100%;border-radius:8px;margin:8px 0" />

**איך משתמשים?**
1. היכנסו ל**הגדרות** ומצאו את **עיצוב המיניסייט**
2. בחרו צבע רקע מהפלטה או בחרו צבע מותאם אישית
3. שלחו את הקישור ללקוח — הוא יראה את המיניסייט המעוצב עם כל הפילטרים החדשים`
  },
  {
    id: 17,
    areas: ["listings"],
    date: '26-03-2026',
    showOnlyForRealEstate: true,
    title: 'היסטוריית מחיר בכרטיסי נכסים',
    content: `### היסטוריית מחיר בכרטיסי נכסים

<img src="/static/_haim/pricing-update.png" alt="היסטוריית מחיר" style="max-width:100%;border-radius:8px;margin:8px 0" />

מעכשיו תוכלו לראות ישירות בכרטיס הנכס אם היתה ירידת מחיר. נכסים שהמחיר שלהם ירד מוצגים עם כפתור מחיר מודגש בצבע כחול ואייקון ירידה.

**מה חדש:**
- **כפתור מחיר אינטראקטיבי** — כשנכס עבר שינוי מחיר, המחיר מוצג ככפתור. לחיצה עליו מציגה את כל היסטוריית המחירים עם תאריכים
- **פילטר חיפוש חדש** — בעמוד **התאמת נכסים**, ניתן לסנן ולהציג רק נכסים שעברו הורדת מחיר
- **גם בהתראות** — האינדיקציה מופיעה גם ברשימת ההתראות בדף הראשי ובעמוד התאמת נכסים ללקוחות

**איך משתמשים?**
1. היכנסו ל**התאמת נכסים** וחפשו נכסים
2. נכסים עם ירידת מחיר יוצגו עם כפתור מחיר כחול — לחצו עליו לצפייה בהיסטוריה
3. לסינון נכסים עם הורדת מחיר בלבד — פתחו את **הפילטרים** והפעילו את **הצג נכסים עם הורדת מחיר בלבד**`
  },
  {
    id: 16,
    areas: ["listings"],
    date: '26-03-2026',
    showOnlyForRealEstate: true,
    title: 'התראות על ירידות מחיר',
    content: `### התראות על ירידות מחיר בנכסים

מעכשיו תוכלו לקבל התראות כשמחיר של נכס יורד, כדי לא לפספס הזדמנויות.

**איך מפעילים?**ּ
1. היכנסו ל**הגדרות**
2. סמנו את האפשרות **קבל התראות על ירידות מחיר בנכסים**`
  },
  {
    id: 15,
    areas: ["messaging","minisites"],
    date: '26-03-2026',
    showOnlyForRealEstate: true,
    title: 'קישור למיניסייט בדיוור',
    content: `### קישור למיניסייט בהודעות דיוור

אפשר עכשיו להוסיף קישור אישי למיניסייט של כל לקוח בתוך הודעות הדיוור ההמוני.

**מה חדש:**
- **שדה דינאמי חדש** — הוסיפו את השדה "קישור למיניסייט" בעת כתיבת הודעה, והמערכת תחליף אותו בקישור האישי של כל לקוח
- **התראה על לקוח ללא מיניסייט** — לקוחות שאין להם מיניסייט מסומנים באזהרה, כדי שתוכלו לערוך את ההודעה שלהם לפני השליחה

**איך משתמשים?**
1. פתחו דיוור המוני מעמוד הלקוחות או מהתאמת נכסים
2. לחצו **הוסף שדה** ובחרו **קישור למיניסייט**
3. הכניסו את השדה לטקסט ההודעה
4. בשלב התצוגה המקדימה — לקוחות ללא מיניסייט יסומנו כדי שתוכלו להתאים את ההודעה`
  },
  {
    id: 14,
    areas: ["listings","customers"],
    date: '26-03-2026',
    showOnlyForRealEstate: true,
    title: 'התאמת קונים מרובה + סינון התראות',
    content: `### התאמת קונים לכמה נכסים במקביל

עד עכשיו, כדי למצוא קונים מתאימים הייתם צריכים ללחוץ על כל נכס בנפרד ולחכות. עכשיו אפשר לבחור עד 20 נכסים מההתראות ולמצוא התאמות לכולם בלחיצה אחת.

**מה חדש:**
- **סינון לפי סוג מודעה** — בוררים בין פרטי, תיווך, או הכל. הסינון נשמר ומופיע גם בדשבורד הראשי וגם בדף התאמת נכס לקונים
- **בחירת מספר נכסים** — לחצו על נכסים מרשימת ההתראות כדי לסמן אותם (עד 20)
- **חיפוש התאמות** — לחצו על "חפש התאמות" ותקבלו תוצאות לכל הנכסים במקביל
- **כרטיס מלא לכל נכס** — לשוניות קונים שלי / קונים של המשרד, הוספה למיניסייט, שיתוף, התקשרות
- **הוספה מרוכזת למיניסייט** — לכל נכס אפשר להוסיף את כל הלקוחות מעל אחוז התאמה מסוים
- **דיוור מרוכז** — כפתור לשליחת הודעה לכל הלקוחות שנוספו למיניסייט במהלך הסשן

**איך משתמשים?**
1. כנסו לעמוד **התאמת נכס לקונים**
2. פתחו את ההתראות ובחרו סוג מודעה (פרטי/תיווך)
3. סמנו נכסים מהרשימה ולחצו **חפש התאמות**
4. עברו על התוצאות, הוסיפו קונים למיניסייט
5. לחצו **שלח הודעה** כדי לדוור את כל הלקוחות שנוספו`
  },
  {
    id: 13,
    areas: ["messaging"],
    date: '22-03-2026',
    title: 'לוג דיוורים',
    content: `### לוג דיוורים

עכשיו אפשר לראות את כל ההודעות שנשלחו מהמערכת במקום אחד מרוכז.

**מה חדש:**
- טבלת לוג מלאה עם כל ההודעות שנשלחו
- סיכום סטטיסטיקות: סה"כ, חודש אחרון, 3/6/12 חודשים
- סינון לפי טווח תאריכים
- צפייה בהודעה המלאה בלחיצה על השורה
- למנהלי ארגון: צפייה בדיוורים של כל המשתמשים בארגון עם אפשרות סינון לפי משתמש

**איך משתמשים?**
1. כנסו לעמוד **שליחת דיוור ב-WhatsApp**
2. לחצו על לשונית **לוג דיוורים**
3. השתמשו בסינון תאריכים לחיפוש ממוקד`
  },
  {
    id: 12,
    areas: ["minisites"],
    date: '18-03-2026',
    showOnlyForRealEstate: true,
    title: 'צפיות במיניסייט',
    content: `### צפיות במיניסייט

כשאתם שולחים קישור לנכסים ללקוח דרך **התאמת נכסים**, המערכת עכשיו עוקבת אחרי הצפיות בקישור ומראה לכם בדיוק מה הלקוח ראה.

**מה חדש:**
- מעקב אוטומטי אחרי פתיחת קישורים ששלחתם ללקוחות
- ספירת צפיות כוללת (פתיחות דף + לחיצות על נכסים)
- ספירת נכסים שונים שהלקוח צפה בהם
- ספירת צפיות לכל נכס ספציפי

**איפה רואים את זה:**
- **דף צפיות במיניסייט** — לוח בקרה מרוכז עם כל הקישורים, סטטוס צפיות, וסיכום נתונים. <a href="/dashboard/links">לחצו כאן לעבור לדף הקישורים</a>
- **כרטיס לקוח** — בלשונית התאמות נכסים, סימון ירוק כשהלקוח צפה בקישור
- **התאמת נכס לקונים** — סימון צפיות ליד כל קונה שהותאם לנכס
- **עריכת רשימת נכסים** — סיכום צפיות בראש הדיאלוג

**איך זה עובד:**
1. שלחו קישור לנכסים ללקוח דרך עמוד **התאמת נכסים**
2. כשהלקוח פותח את הקישור, המערכת סופרת את הצפייה
3. כשהלקוח לוחץ על נכס ספציפי, גם זה נספר
4. כנסו לעמוד **קישורים** בתפריט לראות את כל הנתונים`
  },
  {
    id: 11,
    areas: ["ai"],
    date: '29-12-2025',
    showOnlyForRealEstate: true,
    title: 'מחולל קריאייטבים חדש',
    content: `### מחולל קריאייטבים 🎨

עכשיו תוכלו ליצור קריאייטבים מקצועיים ומותאמים אישית!

**מה חדש:**
- מחולל קריאייטבים מתקדם עם תבניות מקצועיות
- התאמה אישית לפי הנכס שלכם
- מגוון עיצובים וסגנונות
- ייצוא באיכות גבוהה

**איך זה עובד:**
1. כנסו לעמוד **מחולל קריאייטבים** בתפריט
2. בחרו תבנית מהמבחר הזמין
3. הזינו את פרטי הנכס
4. קבלו קריאייטב מותאם אישית

**דוגמה לתבנית:**

<div style="text-align: center; margin: 20px 0;">
  <img src="/static/creative-template-display-images/real-estate-just-sold-1.jpg" alt="דוגמה לתבנית קריאייטב" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />
</div>

**יתרונות:**
- חיסכון בזמן ובעלויות עיצוב
- מראה מקצועי ועקבי
- קלות שימוש מרבית
- התאמה מושלמת לשוק הישראלי

התחילו ליצור קריאייטבים מרשימים עוד היום!`
  },
  {
    id: 5,
    areas: ["listings"],
    date: '12-08-2025',
    showOnlyForRealEstate: true,
    title: 'ניהול רשימות נכסים חדש',
    content: `### ניהול רשימות נכסים 🏠

זמין בעמוד **התאמת נכסים**.

הוספנו יכולת ליצור רשימות נכסים:
- יצירת רשימות חדשות
- הוספה/הסרה של נכסים לרשימות
- עריכת שם הרשימה ומחיקתה
- צפיה ברשימות קיימות

**איך משתמשים?**
1. כנסו לעמוד התאמת נכסים
2. כנסו למצב בחירה מרובה 
3. בחרו נכסים ולחצו על הכפתור הוספה לרשימה
4. בחרו רשימה אחת או יותר שאליה הנכסים יתוופסו
`
  },
  {
    id: 6,
    areas: ["listings"],
    date: '12-08-2025',
    showOnlyForRealEstate: true,
    title: 'חיפוש בכמה שכונות',
    content: `### חיפוש בכמה שכונות וערים 🔍

זמין בעמוד **התאמת נכסים**.

חיפוש נכסים במספר שכונות וערים במקביל:
- הוספת שכונות ממקומות שונים
- תוצאות מאוחדות בחיפוש אחד
- השוואה קלה בין אזורים

**איך משתמשים?**
1. כנסו לעמוד התאמת נכסים
2. בחרו שכונות באותה עיר או בערים שונות
3. קבלו את כל התוצאות בחיפוש אחד`
  },
  {
    id: 7,
    areas: ["listings"],
    date: '21-08-2025',
    showOnlyForRealEstate: true,
    title: 'שיתוף פעולה עם מדלן',
    content: `### שיתוף פעולה עם מדלן

התחברנו עם מדלן כדי להביא לכם את המידע המעודכן ביותר על נכסים בישראל:

- עכשיו תוכלו לראות גם נכסים שמגיעים ממדלן

- אפשר לסנן כדי לראות רק נכסים של מדלן

- מידע אמין ומעודכן ישירות מהמקור

- ועוד שדרוגים מרגשים בדרך – זו רק ההתחלה!

<div style="text-align: center; margin: 5px 0;">
    <div style="display: flex; align-items: center; justify-content: center; gap: 5px; margin: 5px 0;">
      <img src="/static/_haim/webot.png" alt="Webot" style="height: 26px; width: auto; color: green;" />
      <div style="display: flex; align-items: center; justify-content: center; color: white; font-size: 2rem; font-weight: bold; text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3); background: rgba(255, 255, 255, 0.1); border-radius: 50%; width: 48px; height: 48px;">🤝</div>
      <img src="/static/icons/madlan_logo_full.svg" alt="Madlan" style="height: 32px; width: auto; color: green;" />
    </div>
  </div>
`
  },
  {
    id: 8,
    areas: ["customers"],
    date: '27-08-2025',
    showOnlyForRealEstate: true,
    title: 'תגיות ללקוחות',
    content: `### תגיות ללקוחות 🏷️

זמין בעמוד **לקוחות**.

הוספנו מערכת תגיות ללקוחות שלכם:

**יכולות חדשות:**
- יצירת תגיות מותאמות אישית
- הוספת תגיות מרובות לכל לקוח
- סינון לקוחות לפי תגיות
- ניהול וארגון טוב יותר של הלקוחות

**איך זה עוזר לכם?**
- **קטגוריזציה טובה יותר**: סווגו את הלקוחות שלכם לפי סוג (קונים, משקיעים, מוכרים)
- **התאמת נכסים מדויקת**: מצאו במהירות את הלקוחות הרלוונטיים לכל נכס
- **מעקב אפקטיבי**: עקבו אחר שלבי הטיפול בלקוחות שונים
- **שיווק ממוקד**: שלחו הודעות רלוונטיות לקבוצות ספציפיות

**איך משתמשים?**
1. כנסו לעמוד לקוחות
2. בחרו לקוח ולחצו על עריכה
3. הוסיפו תגיות רלוונטיות
4. השתמשו בסינון כדי למצוא לקוחות לפי תגיות

עכשיו תוכלו לנהל את הלקוחות שלכם בצורה מקצועית ויעילה יותר!`
  },
  {
    id: 9,
    areas: ["minisites"],
    date: '27-08-2025',
    showOnlyForRealEstate: true,
    title: 'הצגת סוכני מדלן מובילים במיניסייט',
    content: `### הצגת סוכני מדלן מובילים

בשיתוף עם מדלן, הוספנו הצגה מיוחדת לסוכני הנדל"ן מובילים:

**מה חדש:**
- תג TOP 10 יוצג באופן אוטומטי במיניסייט שלכם
- לקוחותיכם יוכלו לראות שאתם סוכן מוביל
- בלחיצה על תמונת הפרופיל ייפתח הפרופיל שלכם במדלן

**איפה לראות:**
1. כנסו ל-**פרופיל** ← **הגדרות** 
2. במיניסייט שלכם

**יתרונות:**
- אמינות מוגברת אצל לקוחות פוטנציאליים
- הבחנה ויזואלית במיניסייט
- קישור לפרופיל המקצועי שלכם במדלן
- חיזוק המותג האישי שלכם

הפרופיל שלכם כבר מעודכן, אם אתם סוכני TOP 10 של מדלן ולא רואים את התג בהגדרות, אנא צרו איתנו קשר
- 📱 בשיחה או בוואטסאפ: 053-420-6477
`
  },
  {
    id: 10,
    areas: ["minisites"],
    date: '21-09-2025',
    showOnlyForRealEstate: true,
    title: 'תמיכה ב-Meta Pixel במיניסייט',
    content: `### Meta Pixel במיניסייט

עכשיו ניתן להטמיע קוד Meta Pixel במיניסייט שלכם!

**מה חדש?**
- אם מוגדר מזהה Meta Pixel (ownerMetaPixelId) למשתמש, הקוד יוטמע אוטומטית בדף המיניסייט
- מאפשר מעקב מתקדם אחרי מבקרים ולקוחות
- מתאים לשיווק ממוקד וניתוח תנועה

**איך זה עובד?**
1. הגדירו מזהה Meta Pixel בפרטי המשתמש
2. הקוד יוטמע אוטומטית בדף המיניסייט
3. התחילו לעקוב אחרי תנועת המבקרים ולשפר את השיווק שלכם

הטמעה זו מאפשרת לכם לקבל נתונים מדויקים יותר ולייעל את הפעילות השיווקית שלכם.`
  }
];
